var $3b1df42c88e582ce$exports = {};
// Focus never leaves the trigger: the arrows move aria-activedescendant, the
// element owns open, and Elm holds nothing but which value is chosen.
const $3b1df42c88e582ce$var$TRIGGER = '[role="combobox"]';
const $3b1df42c88e582ce$var$LISTBOX = '[role="listbox"]';
const $3b1df42c88e582ce$var$OPTION = '[role="option"]';
const $3b1df42c88e582ce$var$PICKABLE = `${$3b1df42c88e582ce$var$OPTION}:not([aria-disabled="true"])`;
// Long enough to finish a word, short enough that the next one starts over.
const $3b1df42c88e582ce$var$TYPEAHEAD_MS = 500;
let $3b1df42c88e582ce$var$selectIds = 0;
class $3b1df42c88e582ce$var$ElmSelect extends HTMLElement {
    connectedCallback() {
        this.selectId ??= `elm-select-${++$3b1df42c88e582ce$var$selectIds}`;
        this.optionIds ??= 0;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('click', this.onClick);
        this.addEventListener('mousedown', this.onMouseDown);
        this.addEventListener('focusout', this.onFocusOut);
        this.wire();
        this.items = new MutationObserver(()=>this.wire());
        this.items.observe(this, {
            childList: true,
            subtree: true
        });
    }
    disconnectedCallback() {
        this.items.disconnect();
        clearTimeout(this.typing);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('mousedown', this.onMouseDown);
        this.removeEventListener('focusout', this.onFocusOut);
    }
    // Ours, not a select rendered inside one of our options.
    own(selector) {
        return [
            ...this.querySelectorAll(selector)
        ].filter((node)=>node.closest('elm-select') === this);
    }
    trigger() {
        return this.own($3b1df42c88e582ce$var$TRIGGER)[0] ?? null;
    }
    listbox() {
        return this.own($3b1df42c88e582ce$var$LISTBOX)[0] ?? null;
    }
    // Every option Elm rendered, in the order it rendered them: the index Elm
    // reads back is a position in that list.
    all() {
        return this.own($3b1df42c88e582ce$var$OPTION);
    }
    // Filtering hides options rather than removing them.
    pickable() {
        return this.own($3b1df42c88e582ce$var$PICKABLE).filter((option)=>option.checkVisibility());
    }
    get open() {
        return this.hasAttribute('open');
    }
    get disabled() {
        return this.trigger()?.getAttribute('aria-disabled') === 'true';
    }
    chosen() {
        return this.pickable().find((option)=>option.getAttribute('aria-selected') === 'true') ?? null;
    }
    wire() {
        const trigger = this.trigger();
        const listbox = this.listbox();
        if (!trigger || !listbox) {
            this.removeAttribute('open');
            return;
        }
        trigger.id ||= `${this.selectId}-trigger`;
        listbox.id ||= `${this.selectId}-listbox`;
        trigger.setAttribute('aria-controls', listbox.id);
        trigger.setAttribute('aria-expanded', String(this.open));
        // A counter rather than a position: Elm reuses option nodes, so a
        // positional id can collide with one an earlier render already minted.
        for (const option of this.all())option.id ||= `${this.selectId}-option-${++this.optionIds}`;
        this.activate(this.active && this.contains(this.active) ? this.active : null);
    }
    activate(option) {
        const trigger = this.trigger();
        for (const other of this.all())other.removeAttribute('data-active');
        this.active = option ?? null;
        if (!this.active) {
            trigger?.removeAttribute('aria-activedescendant');
            return;
        }
        this.active.setAttribute('data-active', '');
        trigger?.setAttribute('aria-activedescendant', this.active.id);
        // A menu tall enough to scroll does not follow the cursor on its own.
        this.active.scrollIntoView?.({
            block: 'nearest'
        });
    }
    openList() {
        if (this.open || this.disabled || !this.listbox()) return;
        this.setAttribute('open', '');
        this.trigger()?.setAttribute('aria-expanded', 'true');
        this.activate(this.chosen() ?? this.pickable()[0]);
    }
    closeList({ returnFocus: returnFocus = true } = {}) {
        if (!this.open) return;
        this.removeAttribute('open');
        this.trigger()?.setAttribute('aria-expanded', 'false');
        this.activate(null);
        if (returnFocus) this.trigger()?.focus();
    }
    // Elm is told which position it rendered, not a value it would have to
    // stringify and parse back.
    commit(option) {
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: false,
            detail: {
                index: this.all().indexOf(option)
            }
        }));
        this.closeList();
    }
    // A native select jumps to what you type, and losing that is the cost of a
    // styled one, so the element keeps a short buffer.
    typeahead(key) {
        clearTimeout(this.typing);
        this.typed = (this.typed ?? '') + key.toLowerCase();
        this.typing = setTimeout(()=>{
            this.typed = '';
        }, $3b1df42c88e582ce$var$TYPEAHEAD_MS);
        const match = this.pickable().find((option)=>option.textContent.trim().toLowerCase().startsWith(this.typed));
        if (match) this.activate(match);
    }
    // The listbox is not focusable, so a mousedown there would blur the trigger
    // and close the menu before the click could land on an option.
    onMouseDown(event) {
        if (this.own($3b1df42c88e582ce$var$LISTBOX).some((listbox)=>listbox.contains(event.target))) event.preventDefault();
    }
    onClick(event) {
        if (event.target.closest('elm-select') !== this) return;
        const trigger = event.target.closest($3b1df42c88e582ce$var$TRIGGER);
        if (trigger) {
            if (this.open) this.closeList();
            else {
                this.openList();
                trigger.focus();
            }
            return;
        }
        const option = event.target.closest($3b1df42c88e582ce$var$PICKABLE);
        if (option) this.commit(option);
    }
    // A click outside blurs the trigger, and focus has already gone where the
    // user sent it, so closing must not take it back.
    onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.closeList({
            returnFocus: false
        });
    }
    onKeyDown(event) {
        if (event.target.closest('elm-select') !== this) return;
        const key = event.key;
        if (!this.open) {
            if (![
                'ArrowDown',
                'ArrowUp',
                'Enter',
                ' '
            ].includes(key)) return;
            event.preventDefault();
            this.openList();
            // Up from closed starts at the end, as a native select does, unless
            // there is already a selection to start from.
            if (key === 'ArrowUp' && !this.chosen()) this.activate(this.pickable().at(-1));
            return;
        }
        if (key === 'Escape') {
            // Ours alone: the modal the form sits in must not close too.
            event.preventDefault();
            event.stopPropagation();
            this.closeList();
            return;
        }
        // Tab leaves the select, so the menu closes and focus carries on.
        if (key === 'Tab') {
            this.closeList({
                returnFocus: false
            });
            return;
        }
        // A native select types a space into the buffer rather than committing,
        // so "dr s" reaches Dr Strange.
        if (key === ' ' && this.typed) {
            event.preventDefault();
            this.typeahead(key);
            return;
        }
        if (key === 'Enter' || key === ' ') {
            event.preventDefault();
            if (this.active) this.commit(this.active);
            else this.closeList();
            return;
        }
        const options = this.pickable();
        if (!options.length) return;
        const step = {
            ArrowDown: 1,
            ArrowUp: -1
        }[key];
        const here = options.indexOf(this.active);
        let next;
        if (step && here === -1) next = step === 1 ? options[0] : options.at(-1);
        else if (step) next = options[(here + step + options.length) % options.length];
        else if (key === 'Home') next = options[0];
        else if (key === 'End') next = options.at(-1);
        else if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
            event.preventDefault();
            this.typeahead(key);
            return;
        } else return;
        // Otherwise the page scrolls under the open menu.
        event.preventDefault();
        this.activate(next);
    }
}
if (!customElements.get('elm-select')) customElements.define('elm-select', $3b1df42c88e582ce$var$ElmSelect);


//# sourceMappingURL=project.3f86a3b4.js.map
